'use client';
import { useWindowOpen } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  TextButton,
  useWindowSize,
  List,
  Divider,
  Svg,
  semanticColor
} from '@repo/ui';
import Link from 'next/link';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import { SERVICE_LINKS } from '@/lib/constants/common';

export const LogoHeaderLeftButtons = () => {
  const { sendClickEvent } = useUserEventLogger();

  return (
    <Link href={'/'} scroll={true} className={'flex items-center py-tiny-medium px-normal-small w-fit h-full'}>
      <img
        src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/brand/logos/logo_bznav-black.svg`}
        className={'flex items-center cursor-pointer w-[69px] h-[24px]'}
        alt={'bznav logo'}
        onClick={() => sendClickEvent('topnav_logo')}
      />
    </Link>
  );
};

export const BrandResourceLogoHeaderLeftButtons = () => {
  const { sendClickEvent } = useUserEventLogger();

  return (
    <Link href={'/'} scroll={true} className={'flex items-center py-tiny-medium px-normal-small w-fit h-full'}>
      <img
        src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/brand/logos/logo_bznav-brand-resource_black.svg`}
        className={'flex items-center cursor-pointer w-[152px] h-[24px]'}
        alt={'bznav logo'}
        onClick={() => sendClickEvent('topnav_logo')}
      />
    </Link>
  );
};

export const LogoHeaderRightButtons = ({
  isOpenMobileMenu,
  setIsOpenMobileMenu
}: {
  isOpenMobileMenu: boolean;
  setIsOpenMobileMenu: Dispatch<SetStateAction<boolean>>;
}) => {
  const { isOverTablet } = useWindowSize();

  useEffect(() => {
    if (isOverTablet && isOpenMobileMenu) {
      setIsOpenMobileMenu(false);
    }
  }, [isOverTablet, isOpenMobileMenu, setIsOpenMobileMenu]);

  return (
    <>
      <SpreadRightButton />
      <IconButton
        key={'mobile-menu-button'}
        className={'transition duration-150 ease-in-out hidden compact:flex'}
        iconType={isOpenMobileMenu ? 'close' : 'menu'}
        onClick={() => setIsOpenMobileMenu(!isOpenMobileMenu)}
      />
    </>
  );
};

const SpreadRightButton = () => {
  const { openWindow } = useWindowOpen();

  return (
    <div className={'flex gap-2 compact:hidden'}>
      <TextButton
        variant={'secondary'}
        size={'small'}
        onClick={() => {
          openWindow(process.env.NEXT_PUBLIC_CARE_DOMAIN || SERVICE_LINKS['bznav-care']);
        }}
      >
        비즈넵 케어
      </TextButton>
      <TextButton
        variant={'secondary'}
        size={'small'}
        onClick={() => {
          openWindow(process.env.NEXT_PUBLIC_REFUND_DOMAIN || SERVICE_LINKS['bznav-refund']);
        }}
      >
        비즈넵 환급
      </TextButton>
      <TextButton
        variant={'secondary'}
        size={'small'}
        onClick={() => {
          openWindow(process.env.NEXT_PUBLIC_SENA_DOMAIN || SERVICE_LINKS['bznav-sena']);
        }}
      >
        비즈넵 세나
      </TextButton>
      <CustomerCenterButton />
    </div>
  );
};

const CustomerCenterButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isButtonOver = useRef(false);
  const isMenuOver = useRef(false);

  const { openWindow } = useWindowOpen();

  const closeMenu = () => {
    setTimeout(() => {
      if (!isMenuOver.current && !isButtonOver.current) {
        setIsOpen(false);
      }
    }, 100);
  };

  return (
    <DropdownMenu modal={false} open={isOpen}>
      <DropdownMenuTrigger asChild>
        <TextButton
          variant={'secondary'}
          size={'small'}
          onMouseEnter={() => {
            isButtonOver.current = true;
            setIsOpen(true);
          }}
          onMouseLeave={() => {
            isButtonOver.current = false;
            closeMenu();
          }}
        >
          고객센터&nbsp;&nbsp;
        </TextButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={'w-48 py-2'}
        align="end"
        onMouseOver={() => {
          isMenuOver.current = true;
          setIsOpen(true);
        }}
        onMouseLeave={() => {
          isMenuOver.current = false;
          closeMenu();
        }}
      >
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => {
              openWindow('https://help.bznav.com/hc/ko');
            }}
          >
            비즈넵 환급 고객센터
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              openWindow('https://care.bznav.com/cs-center');
            }}
          >
            비즈넵 케어 고객센터
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              openWindow('https://help.bznav.com/hc/ko/categories/900000383946');
            }}
          >
            세무 지식센터
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const MobileMenu = ({ isOpenMobileMenu }: { isOpenMobileMenu: boolean }) => {
  const [displayClassName, setDisplayClassname] = useState('hidden');
  const { openWindow } = useWindowOpen();

  const navClassName = useMemo(() => {
    const state = isOpenMobileMenu ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none';
    return `bg-neutral-background-dimmed-low backdrop-blur-[20px] fixed left-0 z-20 h-screen w-full overflow-y-auto transition-all ease-out duration-200 transform translate-y-0 body-small-medium medium:!hidden expanded:!hidden ${state} ${displayClassName}`;
  }, [displayClassName, isOpenMobileMenu]);

  const mobileMenus = [
    {
      link: SERVICE_LINKS['bznav-care'],
      label: 'bzc;'
    },
    {
      link: SERVICE_LINKS['bznav-refund'],
      label: 'bzr;'
    },
    {
      link: SERVICE_LINKS['bznav-sena'],
      label: 'bzs;'
    }
  ];

  useEffect(() => {
    const html = document.documentElement;
    if (isOpenMobileMenu) {
      html.classList.add('no-scroll');
      setDisplayClassname('block');
    } else {
      html.classList.remove('no-scroll');
      setTimeout(() => {
        setDisplayClassname('hidden');
      }, 200);
    }

    return () => {
      html.classList.remove('no-scroll');
    };
  }, [isOpenMobileMenu]);

  return (
    <nav className={navClassName}>
      <ul className={'bg-white m-0 list-none body-small-medium px-5 py-4'}>
        {mobileMenus.map((menu, index) => (
          <>
            <List
              key={index}
              size={'medium'}
              primaryAction
              onClick={() => {
                openWindow(menu.link);
              }}
              title={
                <div>
                  {menu.label} <span className="pl-tiny-small body-small-medium text-neutral-foreground-lowest">바로가기</span>
                </div>
              }
              supportingElement={
                <div className="flex items-center justify-center">
                  <Svg src={'ic_arrow-up-right.svg'} width={20} height={20} color={semanticColor.neutral.foreground.low} />
                </div>
              }
            />
            {index !== mobileMenus.length - 1 && <Divider key={index} size={'medium'} className="mt-0 mb-0" />}
          </>
        ))}
      </ul>
    </nav>
  );
};
